// Lines that cross, drawn so they read as not connected: where two segments
// cross (`segCross`), a line stroked with a semicircular hump over each crossing
// (`strokeWithHumps`), and a schematic line style's dash (`dashArray`). Shared
// by the plan's connections, a sheet's connections and leaders, and the
// electrical schematic's wires (elecLayout.ts finds its crossings with
// `segCross`, elecDraw.ts strokes them). DOM-free: it only draws on the context
// it is given.

import type {
  SchematicLineStyle,
  Vec2,
} from '../shared/types';

/** Proper interior intersection point of segments p1p2 and p3p4, or null. Near
 *  an end does not count: within 1 % of either segment, or — `endGap` — within
 *  that distance of its ends. */
export function segCross(p1: Vec2, p2: Vec2, p3: Vec2, p4: Vec2, endGap?: number): Vec2 | null {
  const d1x = p2.x - p1.x, d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x, d2y = p4.y - p3.y;
  const denom = d1x * d2y - d1y * d2x;
  if (Math.abs(denom) < 1e-9) return null; // parallel / degenerate
  const t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / denom;
  const u = ((p3.x - p1.x) * d1y - (p3.y - p1.y) * d1x) / denom;
  const e1 = endGap == null ? 0.01 : endGap / (Math.hypot(d1x, d1y) || 1), e2 = endGap == null ? 0.01 : endGap / (Math.hypot(d2x, d2y) || 1);
  if (t <= e1 || t >= 1 - e1 || u <= e2 || u >= 1 - e2) return null; // endpoints don't count
  return { x: p1.x + t * d1x, y: p1.y + t * d1y };
}

/**
 * Stroke a screen-space line a→b, bulging into a small semicircular hump at each
 * given hop point (where it jumps over a crossing line of the same discipline).
 */
export function strokeWithHumps(ctx: CanvasRenderingContext2D, a: Vec2, b: Vec2, hopPts: Vec2[], r: number): void {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len, uy = dy / len;
  const ts = hopPts
    .map((h) => (h.x - a.x) * ux + (h.y - a.y) * uy)
    .filter((t) => t > r && t < len - r)
    .sort((p, q) => p - q);
  // Dashed straight runs, leaving a gap where each hump arc will sit.
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  for (const t of ts) {
    ctx.lineTo(a.x + ux * (t - r), a.y + uy * (t - r)); // up to where the hump starts
    ctx.moveTo(a.x + ux * (t + r), a.y + uy * (t + r)); // resume past the hump
  }
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
  // The hump arcs are drawn solid so a crossing reads clearly as "not connected".
  if (ts.length) {
    ctx.save();
    ctx.setLineDash([]);
    ctx.beginPath();
    const ang0 = Math.atan2(-uy, -ux); // toward the "before" point
    const ang1 = Math.atan2(uy, ux); // toward the "after" point
    for (const t of ts) {
      const cx = a.x + ux * t, cy = a.y + uy * t; // crossing centre on the line
      ctx.moveTo(cx + r * Math.cos(ang0), cy + r * Math.sin(ang0));
      ctx.arc(cx, cy, r, ang0, ang1, true); // semicircle bulging to one side
    }
    ctx.stroke();
    ctx.restore();
  }
}

/** Canvas dash pattern (screen px) for a schematic line style. */
export function dashArray(style: SchematicLineStyle, scale: number): number[] {
  switch (style) {
    case 'dashed': return [9 * scale, 6 * scale];
    case 'dotted': return [1.5 * scale, 4 * scale];
    case 'dash-dot': return [9 * scale, 4 * scale, 1.5 * scale, 4 * scale];
    default: return [];
  }
}
